import React, { useMemo } from "react";
import { formatDate, toPascalCase } from "../utils/helpers";
import { useNavigate } from "react-router-dom";
import PageLayout from "../ui/PageLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useGetProposals } from "../features/proposal/useGetProposals";
import ButtonHelp from "../ui/ButtonHelp";
import CardLoader from "@/ui/CardLoader";
import { HiPencil, HiTrash } from "react-icons/hi2";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useOptimisticRemove } from "../hooks/useOptimisticRemove";
import { deleteProposal } from "../services/apiEditor";
import { useQueryClient } from "@tanstack/react-query";
import { useGetStreamingProposals } from "@/features/proposal/useGetUserStreamingProposals";
import { Separator } from "@/components/ui/separator";
import { AnimatedShinyText } from "@/components/ui/animated-shiny-text";
import { Check, Eye, Sparkles } from "lucide-react";

const steps = [
  {
    title: "Proposal Drafts",
    content:
      "Here are your saved proposal drafts. Click the edit button to modify them",
    target: "#proposal-drafts",
    placement: "centre",
    disableBeacon: true,
  },
];

function Proposals() {
  const {
    data: DBproposals,
    isLoading: isProposalsLoading,
    isSuccess,
  } = useGetProposals();
  const { data: streamingProposals, isLoading: isStreamingProposalsLoading } =
    useGetStreamingProposals();

  const finalProposals = useMemo(() => {
    if (isSuccess && Array.isArray(DBproposals)) {
      const modifier = -1;
      // sort proposals by last modified date
      return DBproposals?.sort((a, b) => {
        const dateA = a.updated_at ? new Date(a.updated_at) : new Date(NaN);
        const dateB = b.updated_at ? new Date(b.updated_at) : new Date(NaN);

        const timeA = isNaN(dateA.getTime())
          ? modifier === 1
            ? Infinity
            : -Infinity
          : dateA.getTime();
        const timeB = isNaN(dateB.getTime())
          ? modifier === 1
            ? Infinity
            : -Infinity
          : dateB.getTime();

        return (timeA - timeB) * modifier;
      });
    }
    return [];
  }, [DBproposals, isSuccess]);
  const isLoading = isProposalsLoading || isStreamingProposalsLoading;
  return (
    <div id="proposal-drafts">
      <PageLayout heading="Proposal Drafts" description="Manage your proposals">
        {isLoading ? (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {Array.from({ length: 3 }).map((_, index) => (
              <CardLoader className="h-96" key={index} />
            ))}
          </div>
        ) : finalProposals.length === 0 && streamingProposals.length === 0 ? (
          <div className="flex flex-col items-center justify-center mt-12 text-center">
            <h2 className="text-2xl font-semibold">No proposals yet :(</h2>
            <p className="text-lg mt-2 max-w-2xl">
              Watch this video tutorial to discover how to draft your first
              government proposal with ease. Then, start exploring opportunities
              to place your first bid.
            </p>
            <iframe
              width="800"
              height="450"
              src="https://www.youtube.com/embed/X0Si5gLJsi4"
              title="SamSearch - AI for Government Contracting"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="mt-6 shadow-lg rounded-lg"
            ></iframe>
            <Button
              className="mt-6 px-6 py-3 text-lg font-semibold"
              onClick={() => window.location.assign("/search")}
            >
              Start by Finding an Opportunity to Place a Bid
            </Button>
          </div>
        ) : (
          <div>
            {streamingProposals?.length > 0 && (
              <div className="flex flex-col gap-4 mt-4">
                <h2 className="text-2xl font-semibold">
                  Currently generating{" "}
                </h2>
                {streamingProposals.map((proposal) => (
                  <StreamingProposalCard
                    key={proposal.proposal_id}
                    proposal={proposal}
                  />
                ))}
                <Separator className="my-4" />
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {finalProposals.map((proposal) => (
                <ProposalCard key={proposal.proposal_id} proposal={proposal} />
              ))}
            </div>
          </div>
        )}
        <ButtonHelp steps={steps} />
      </PageLayout>
    </div>
  );
}

function ProposalCard({ proposal }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const removeFilterFn = (temp) => {
    return temp.proposal_id !== proposal.proposal_id;
  };
  const { mutate: deleteProposalOptimistic } = useOptimisticRemove(
    ["proposalsContracts"],
    deleteProposal,
    removeFilterFn
  );
  const handleClick = () => {
    queryClient.invalidateQueries([
      "editorContent",
      proposal.proposal_id,
      proposal.search_type,
    ]);

    navigate(
      `/contract/${proposal.search_type}/${proposal.proposal_id}/generate/editor`
    );
  };

  return (
    <Card className="w-full flex flex-col justify-between gap-4">
      <CardHeader
        className="hover:cursor-pointer hover:underline"
        onClick={handleClick}
      >
        <CardTitle>{toPascalCase(proposal.title)}</CardTitle>

        {proposal?.updated_at && (
          <CardDescription>
            Last modified {formatDate(proposal.updated_at)}
          </CardDescription>
        )}
      </CardHeader>
      <CardFooter className="flex justify-between items-center">
        <Button className="flex gap-2" onClick={handleClick}>
          <HiPencil className="" />
          Edit
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="secondary" className="flex gap-2">
              <HiTrash className="" />
              Delete
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone and will permanently delete your
                document.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant={"destructive"}
                onClick={() =>
                  deleteProposalOptimistic({
                    proposal_id: proposal.proposal_id,
                    search_type: proposal.search_type,
                  })
                }
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardFooter>
    </Card>
  );
}

function StreamingProposalCard({ proposal }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleClick = () => {
    queryClient.invalidateQueries([
      "editorContent",
      proposal.proposal_id,
      proposal.search_type,
    ]);
    navigate(
      `/contract/${proposal.search_type}/${proposal.proposal_id}/generate/editor`
    );
  };

  return (
    <Card className="w-full">
      <CardHeader
        className="flex flex-row justify-between items-center gap-4"
        onClick={() =>
          navigate(
            `/contract/${proposal.search_type}/${proposal.proposal_id}/generate/editor`
          )
        }
      >
        <CardTitle className="hover:cursor-pointer hover:underline">
          {toPascalCase(proposal.title)}
        </CardTitle>
        <Button
          className="flex gap-2 no-underline"
          size="sm"
          onClick={handleClick}
        >
          <Eye className="" />
          View
        </Button>
      </CardHeader>
      <CardContent className="flex gap-4"></CardContent>
      <CardFooter className="flex justify-between items-center">
        {proposal?.status === "streaming" ? (
          <div
            className={`text-sm rounded-full inline-flex justify-between items-center px-4 py-1 bg-secondary `}
          >
            <AnimatedShinyText
              className={
                "text-sky-700/70 via-sky-950 dark:text-sky-300/70 dark:via-white"
              }
            >
              <div className="flex items-center gap-2">
                <Sparkles className="animate-pulse w-4 h-4" />
                {proposal?.current_section ? (
                  <span>
                    Generating section: <b>{proposal.current_section}</b>
                  </span>
                ) : (
                  "Generating proposal"
                )}
              </div>
            </AnimatedShinyText>
          </div>
        ) : (
          <div
            className={`text-sm rounded-full inline-flex justify-between items-center px-4 py-1 bg-success`}
          >
            <div className="flex items-center gap-2">
              <Check className="animate-pulse w-4 h-4" />
              <span>Proposal is ready</span>
            </div>
          </div>
        )}
      </CardFooter>
    </Card>
  );
}

export default Proposals;
